'use client';

import { Fragment, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Info, ShieldCheck, TrendingUp, Search, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { SHORTLIST_ETFS, EXTENDED_ETFS, type Etf, type EtfCategory } from '@/lib/learn/etfs';
import { HISTORICAL_PERFORMANCE } from '@/lib/learn/historicalPerformance';
import { cn } from '@/lib/cn';

function EtfCard({ etf, index }: { etf: Etf; index: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      whileHover={{ y: -2 }}
      className="rounded-2xl p-5 border border-[var(--color-border-subtle)] flex flex-col"
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-[var(--color-text)]">{etf.ticker}</span>
            <span
              className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: etf.region === 'US'
                  ? 'color-mix(in srgb, #3b82f6 18%, transparent)'
                  : 'color-mix(in srgb, #ef4444 18%, transparent)',
                color: etf.region === 'US' ? '#60a5fa' : '#f87171',
              }}
            >
              {etf.region === 'US' ? '🇺🇸 US' : '🇨🇦 CA'}
            </span>
          </div>
          <p className="text-xs mt-1 leading-snug" style={{ color: 'var(--color-text-muted)' }}>
            {etf.name}
          </p>
        </div>
        <span
          className="text-[10px] font-semibold px-2 py-1 rounded-lg flex-shrink-0"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-accent) 12%, transparent)',
            color: 'var(--color-accent-muted)',
          }}
        >
          {etf.category}
        </span>
      </div>

      <p className="text-sm mt-4 leading-relaxed flex-1" style={{ color: 'var(--color-text-secondary)' }}>
        <span className="font-semibold text-[var(--color-text)]">What it tracks: </span>
        {etf.whatItTracks}
      </p>

      <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
        <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
          Expense ratio
        </p>
        <p className="text-sm font-semibold text-[var(--color-text)]">
          {etf.expenseRatio.toFixed(2)}%
        </p>
      </div>
    </motion.article>
  );
}

const CATEGORY_ORDER: EtfCategory[] = ['Broad Market', 'International', 'Sector', 'Bonds'];

type CategoryFilter = 'All' | EtfCategory;

const CATEGORY_FILTERS: CategoryFilter[] = ['All', 'Broad Market', 'International', 'Sector', 'Bonds'];

function ExtendedTable({ etfs }: { etfs: Etf[] }) {
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('All');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return etfs.filter((etf) => {
      if (categoryFilter !== 'All' && etf.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        etf.ticker.toLowerCase().includes(q) ||
        etf.name.toLowerCase().includes(q)
      );
    });
  }, [etfs, query, categoryFilter]);

  const grouped = useMemo(() => {
    const map = new Map<EtfCategory, Etf[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const etf of filtered) map.get(etf.category)?.push(etf);
    return map;
  }, [filtered]);

  const hasResults = filtered.length > 0;

  return (
    <div className="mt-6 rounded-2xl border border-[var(--color-border-subtle)] overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
      <div
        className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3"
        style={{ backgroundColor: 'var(--color-surface-secondary)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div className="relative flex-1 min-w-0">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: 'var(--color-text-muted)' }}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by ticker or name"
            aria-label="Search ETFs by ticker or name"
            className="w-full pl-9 pr-9 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text)',
            }}
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors cursor-pointer"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div role="radiogroup" aria-label="Filter by category" className="flex items-center gap-1 flex-wrap">
          {CATEGORY_FILTERS.map((cat) => {
            const active = categoryFilter === cat;
            return (
              <button
                key={cat}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setCategoryFilter(cat)}
                className="px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                style={
                  active
                    ? { backgroundColor: 'var(--color-accent)', color: '#fff' }
                    : { backgroundColor: 'transparent', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
                }
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-surface-secondary)', color: 'var(--color-text-muted)' }}>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Ticker</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider hidden md:table-cell">Region</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider hidden lg:table-cell">What it tracks</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Expense</th>
            </tr>
          </thead>
          <tbody>
            {!hasResults ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  No ETFs match your search.
                </td>
              </tr>
            ) : (
              CATEGORY_ORDER.map((cat) => {
                const rows = grouped.get(cat) ?? [];
                if (rows.length === 0) return null;
                return (
                  <Fragment key={cat}>
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                        style={{
                          backgroundColor: 'var(--color-bg)',
                          color: 'var(--color-accent-muted)',
                          borderTop: '1px solid var(--color-border-subtle)',
                        }}
                      >
                        {cat}
                      </td>
                    </tr>
                    {rows.map((etf) => (
                      <tr key={etf.ticker} style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                        <td className="px-4 py-3 font-bold text-[var(--color-text)]">{etf.ticker}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--color-text-secondary)' }}>{etf.name}</td>
                        <td className="px-4 py-3 hidden md:table-cell" style={{ color: 'var(--color-text-muted)' }}>{etf.region}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs" style={{ color: 'var(--color-text-muted)' }}>{etf.whatItTracks}</td>
                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: 'var(--color-text)' }}>{etf.expenseRatio.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EquityVsBondExplainer() {
  const last = HISTORICAL_PERFORMANCE[HISTORICAL_PERFORMANCE.length - 1];
  const first = HISTORICAL_PERFORMANCE[0];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="rounded-2xl p-6 border border-[var(--color-border-subtle)]"
        style={{ backgroundColor: 'var(--color-surface)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl"
            style={{ backgroundColor: 'color-mix(in srgb, #10b981 18%, transparent)', color: '#10b981' }}
          >
            <TrendingUp size={18} />
          </div>
          <h4 className="text-base font-semibold text-[var(--color-text)]">Equity ETFs</h4>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          Hold hundreds or thousands of stocks. You own a slice of real companies, so you share in their
          growth. Over long periods equities return more than bonds, but they swing hard — dropping 20&#8211;50%
          in a bad year is normal.
        </p>
        <ul className="mt-3 space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <li>• Higher long-term expected return (~7&#8211;10% per year)</li>
          <li>• Much bigger ups and downs along the way</li>
          <li>• Best when you have many years before you need the money</li>
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="rounded-2xl p-6 border border-[var(--color-border-subtle)]"
        style={{ backgroundColor: 'var(--color-surface)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl"
            style={{ backgroundColor: 'color-mix(in srgb, #3b82f6 18%, transparent)', color: '#60a5fa' }}
          >
            <ShieldCheck size={18} />
          </div>
          <h4 className="text-base font-semibold text-[var(--color-text)]">Bond ETFs</h4>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          Hold baskets of bonds — loans to governments and companies that pay regular interest.
          Returns are lower but much steadier, and bonds often hold their ground (or rise) when
          stocks fall. That&apos;s why they&apos;re used to cushion a portfolio.
        </p>
        <ul className="mt-3 space-y-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <li>• Lower long-term expected return (~3&#8211;5% per year)</li>
          <li>• Small drawdowns in most years</li>
          <li>• Best when you want stability or income</li>
        </ul>
      </motion.div>

      <p className="md:col-span-2 text-sm px-1" style={{ color: 'var(--color-text-secondary)' }}>
        Over the last 50 years ({first.year}&#8211;{last.year}), $100 invested in a broad US equity ETF would have
        grown to roughly <span className="font-semibold text-[var(--color-text)]">${Math.round(last.equity).toLocaleString('en-US')}</span>,
        while the same $100 in a US aggregate bond ETF would be around <span className="font-semibold text-[var(--color-text)]">${Math.round(last.bonds).toLocaleString('en-US')}</span> —
        but notice how much smoother the bond line is.
      </p>
    </div>
  );
}

function HistoricalChart() {
  return (
    <div className="rounded-2xl p-6 border border-[var(--color-border-subtle)]" style={{ backgroundColor: 'var(--color-surface)' }}>
      <div className="flex items-start gap-3 mb-4">
        <div
          className="inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
          style={{ backgroundColor: 'color-mix(in srgb, #64748b 22%, transparent)', color: '#94a3b8' }}
        >
          <ShieldCheck size={20} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-[var(--color-text)]">
            50 years of history: equity vs. bond ETFs
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Growth of $100 invested in each, 1975&#8211;2025. You can see equities ending much higher,
            but also the sharp drawdowns along the way (2000&#8211;2002, 2008, 2022). Bonds grow more slowly
            but much more steadily.
          </p>
        </div>
      </div>

      <div className="h-80 w-full" aria-label="50-year historical equity versus bond ETF growth chart">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={HISTORICAL_PERFORMANCE} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
            <XAxis
              dataKey="year"
              stroke="var(--color-text-muted)"
              fontSize={11}
              tickCount={6}
            />
            <YAxis
              stroke="var(--color-text-muted)"
              fontSize={11}
              tickFormatter={(v) => {
                if (v >= 1000) return `$${(v / 1000).toFixed(0)}k`;
                return `$${v}`;
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: '0.5rem',
                fontSize: '0.75rem',
              }}
              formatter={(v, name) => [`$${Math.round(v as number).toLocaleString('en-US')}`, name]}
              labelFormatter={(label) => `Year ${label}`}
            />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
            <Line
              type="monotone"
              dataKey="equity"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              name="Equity ETF (S&P 500)"
            />
            <Line
              type="monotone"
              dataKey="bonds"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              name="Bond ETF (US Aggregate)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] mt-3 italic" style={{ color: 'var(--color-text-muted)' }}>
        Approximate historical total returns. Values are rounded and indexed to $100 at the start of 1975.
      </p>
    </div>
  );
}

export function EtfGuide() {
  const [expanded, setExpanded] = useState(false);

  const additionalCount = EXTENDED_ETFS.length;

  return (
    <section id="etfs" className="py-16 scroll-mt-24" style={{ backgroundColor: 'var(--color-surface-secondary)' }}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-8"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: 'var(--color-accent-muted)' }}
          >
            2 · ETFs
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
            ETFs: instant diversification in one ticker
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-2xl p-6 border border-[var(--color-border-subtle)] mb-10"
          style={{ backgroundColor: 'var(--color-surface)' }}
        >
          <div className="flex items-start gap-3">
            <div
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
              style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)' }}
            >
              <Info size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-text)]">What&apos;s an ETF?</h3>
              <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                An <span className="font-semibold text-[var(--color-text)]">ETF</span> (exchange-traded fund) is a basket
                of many stocks or bonds bundled into a single ticker. Buying one share gets you instant exposure to
                everything inside, the easiest way to build a diversified portfolio without picking individual stocks.
              </p>
            </div>
          </div>
        </motion.div>

        <h3 className="text-xl font-semibold text-[var(--color-text)] mb-4">Eight ETFs worth knowing</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SHORTLIST_ETFS.map((etf, i) => (
            <EtfCard key={etf.ticker} etf={etf} index={i} />
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="extended-etfs"
            className={cn(
              'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer',
              'border border-[var(--color-border)]'
            )}
            style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-surface)' }}
          >
            {expanded ? 'Hide extended list' : `See ${additionalCount} more ETFs`}
            <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={16} />
            </motion.span>
          </button>
        </div>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              id="extended-etfs"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <ExtendedTable etfs={EXTENDED_ETFS} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12">
          <h3 className="text-xl font-semibold text-[var(--color-text)] mb-4">
            Equity ETFs vs. bond ETFs
          </h3>
          <EquityVsBondExplainer />
          <HistoricalChart />
        </div>
      </div>
    </section>
  );
}
